// RestaurantStatus on the API (restaurants and franchises): it never sends
// "inactive" (SFRESTO-140).
export type RestaurantStatus = "active" | "disabled" | "demo" | "deleted";

import { Media } from "../media/media.type";
import { User } from "../user/user.type";

export interface Country {
  id: string;
  iso2: string;
  phonePrefix: string;
}

export interface Currency {
  id: string;
  iso3: string;
}

export interface Restaurant {
  id: string;
  name: string;
  address: string;
  status: RestaurantStatus;
  created_at: string;
  settings?: RestaurantSettings;
  image: Media;
  shouldAskForWaiterName: boolean;
  country: Country;
  currency: Currency;
}

export interface RestaurantSettings {
  orderNumberingStrategy: OrderNumberingStrategy;
  purchaseOrderNumbering?: PurchaseOrderNumbering;
  shouldAskForWaiterName: boolean;
  /** When true, the POS asks for the number of guests served on dine-in orders. */
  shouldAskForGuestCount?: boolean;
  allowOrderPlacement?: boolean;
  collectCustomerDataUpfront?: boolean;
  /** When true, ingredient stock is automatically deducted as orders are placed. */
  trackIngredientStock?: boolean;
}

export interface PurchaseOrderNumbering {
  prefix?: string;
  padding?: number;
}

export interface OrderNumberingStrategy {
  type: OrderNumberingStrategyType;
  prefix?: string;
  padding?: number;
  startNumber?: number;
  dateFormat?: OrderNumberingStrategyDateFormat;
  resetFrequency: OrderNumberingStrategyResetFrequency;
}

export enum OrderNumberingStrategyType {
  Date = "date_based",
  Sequential = "sequential",
}

export enum OrderNumberingStrategyResetFrequency {
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
  Yearly = "yearly",
}

export enum OrderNumberingStrategyDateFormat {
  DDMMYYYY = "dmY",
  MMDDYYYY = "mdY",
  YYYYMMDD = "Ymd",
  DDMMYYYYHHMM = "dmYHi",
  MMDDYYYYHHMM = "mdYHi",
  YYYYMMDDHHMM = "YmdHi",
}

export type RestaurantFormData = Pick<
  Restaurant,
  "name" | "address" | "settings"
> & {
  imageUrl: File;
  newImageUrl: string;
  image: string;
};

export interface RestaurantFranchise {
  id: string;
  name: string;
  address: string;
  status: RestaurantStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  owner: User;
}

export interface Employee {
  id: string;
  fullname: string;
  image: Media;
}

export interface FranchiseOwner {
  id: string;
  owner: User;
  isPrimary: boolean;
  franchise: RestaurantFranchise;
}

export interface RestaurantManager {
  id: string;
  manager: User;
  isPrimary: boolean;
  restaurant: Restaurant;
}

export interface AddFranchiseOwnerPayload {
  phone: string;
  fullName?: string;
}

export interface AddRestaurantManagerPayload {
  phone: string;
  fullName?: string;
}

export interface FranchiseUpdatePayload {
  name?: string;
}
